import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Calendar,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Package,
  Tag,
  Trophy,
  Users,
  Volleyball,
} from 'lucide-react';
import type { AcademySetupPageTourKey, UserPreferences } from '@court-sync/shared';
import type { TourStep } from '@/components/ui/Tour';
import { academySettingsTourSteps } from './academy-settings-tour-steps';
import {
  academyLessonsTourSteps,
  termCreateTourSteps,
  termLessonCreateTourSteps,
  termManageTourSteps,
} from './academy-page-tour-steps';
import { campSingleCreateTourSteps } from './camp-single-create-tour-steps';
import { programCreateTourSteps } from './program-create-tour-steps';
import { bookingsListTourSteps } from './bookings-management-tour-steps';
import { coachCreateTourSteps } from './coach-create-tour-steps';
import { playEventCreateTourSteps } from './play-event-create-tour-steps';
import { playEventSeriesCreateTourSteps } from './play-event-series-create-tour-steps';
import { lessonSessionDetailTourSteps } from './lesson-session-detail-tour-steps';
import {
  paymentsBillingReportTourSteps,
  paymentsDashboardTourSteps,
  paymentsDiscountsTourSteps,
  paymentsPromoCodesTourSteps,
  paymentsSettingsTourSteps,
  paymentsStarterTourSteps,
} from './payments-management-tour-steps';
import {
  studentDetailTourSteps,
} from './students-management-tour-steps';
import { clubCalendarTourSteps } from './club-calendar-tour-steps';
import {
  clubCourtHireBookingsTourSteps,
  clubCourtHireRatesTourSteps,
  clubCourtHireSettingsTourSteps,
  courtHireRateCreateTourSteps,
} from './club-court-hire-tour-steps';
import {
  clubMembersRosterTourSteps,
  clubMembersSettingsTourSteps,
  clubMembersTypesTourSteps,
  membershipTypeCreateTourSteps,
} from './club-members-tour-steps';
import {
  clubInventoryHiresTourSteps,
  clubInventoryItemsTourSteps,
  inventoryItemCreateTourSteps,
} from './club-inventory-tour-steps';

/** Checklist-linked keys plus page-specific sub-tours (e.g. terms create vs manage). */
export type PageTourKey =
  | AcademySetupPageTourKey
  | 'academy-profile'
  | 'academy-public-site'
  | 'academy-sports'
  | 'academy-terms-create'
  | 'academy-programs-create'
  | 'academy-camps-create'
  | 'academy-terms-lesson-create'
  | 'academy-terms-manage'
  | 'academy-lesson-session'
  | 'academy-coaches-create'
  | 'play-events-create'
  | 'play-events-series-create'
  | 'academy-bookings-list'
  | 'academy-payments-billing-report'
  | 'academy-payments-starter'
  | 'academy-payments-dashboard'
  | 'academy-payments-settings'
  | 'academy-payments-discounts'
  | 'academy-payments-promo-codes'
  | 'academy-settings'
  | 'organization-settings'
  | 'academy-students-list'
  | 'academy-student-detail'
  | 'club-calendar'
  | 'club-court-hire'
  | 'club-court-hire-bookings'
  | 'club-court-hire-rates'
  | 'club-court-hire-settings'
  | 'club-court-hire-rate-create'
  | 'club-members'
  | 'club-members-roster'
  | 'club-members-types'
  | 'club-members-settings'
  | 'club-members-type-create'
  | 'club-inventory'
  | 'club-inventory-items'
  | 'club-inventory-hires'
  | 'club-inventory-item-create';

export interface PageTourDefinition {
  key: PageTourKey;
  steps: TourStep[];
  dialogTitle: string;
  dialogDescription: string;
  dialogIcon: LucideIcon;
  legacyPreferenceKey?: keyof UserPreferences;
  /** Parent checklist key with no steps; pages pick the active sub-tour on first visit. */
  deferToPage?: boolean;
}

export const PAGE_TOUR_REGISTRY: Record<PageTourKey, PageTourDefinition> = {
  'organization-settings': {
    key: 'organization-settings',
    steps: [],
    dialogTitle: 'Quick tour: Organization settings',
    dialogDescription:
      'Contact details, public site, sports, and player ratings across all tabs.',
    dialogIcon: Building2,
    deferToPage: true,
  },
  'academy-profile': {
    key: 'academy-profile',
    steps: [],
    dialogTitle: 'Quick tour: Organization profile',
    dialogDescription: 'Learn where to add contact details, branding, and organization settings.',
    dialogIcon: Building2,
    deferToPage: true,
  },
  'academy-public-site': {
    key: 'academy-public-site',
    steps: [],
    dialogTitle: 'Quick tour: Public site',
    dialogDescription: 'Configure your public URL, branding, and directory visibility.',
    dialogIcon: Building2,
    deferToPage: true,
  },
  'academy-sports': {
    key: 'academy-sports',
    steps: [],
    dialogTitle: 'Quick tour: Sports',
    dialogDescription: 'See how to enable sports and set your primary racquet sport.',
    dialogIcon: Volleyball,
    deferToPage: true,
  },
  'academy-coaches-create': {
    key: 'academy-coaches-create',
    steps: coachCreateTourSteps,
    dialogTitle: 'Quick tour: Add a coach',
    dialogDescription: 'Walk through contact details, hourly rate, and saving a new coach.',
    dialogIcon: Users,
  },
  'play-events-create': {
    key: 'play-events-create',
    steps: playEventCreateTourSteps,
    dialogTitle: 'Quick tour: New play event',
    dialogDescription: 'Walk through format, schedule, registration, and courts for a new event.',
    dialogIcon: Trophy,
  },
  'play-events-series-create': {
    key: 'play-events-series-create',
    steps: playEventSeriesCreateTourSteps,
    dialogTitle: 'Quick tour: New play series',
    dialogDescription: 'Walk through weekly schedule, social pairing, and auto-generated sessions.',
    dialogIcon: Trophy,
  },
  'academy-terms-create': {
    key: 'academy-terms-create',
    steps: termCreateTourSteps,
    dialogTitle: 'Quick tour: Create a term',
    dialogDescription: 'Walk through the new-term form — name, dates, and optional lesson copy.',
    dialogIcon: Calendar,
  },
  'academy-terms-lesson-create': {
    key: 'academy-terms-lesson-create',
    steps: termLessonCreateTourSteps,
    dialogTitle: 'Quick tour: Add a term lesson',
    dialogDescription: 'Walk through scheduling, staff, pricing, and capacity for a new term lesson.',
    dialogIcon: CalendarDays,
  },
  'academy-terms-manage': {
    key: 'academy-terms-manage',
    steps: termManageTourSteps,
    dialogTitle: 'Quick tour: Term lessons',
    dialogDescription: 'Add lessons, switch views, and manage your term schedule.',
    dialogIcon: CalendarDays,
  },
  'academy-programs-create': {
    key: 'academy-programs-create',
    steps: programCreateTourSteps,
    dialogTitle: 'Quick tour: New program',
    dialogDescription: 'Walk through pathway basics, skills, and saving a program.',
    dialogIcon: Trophy,
  },
  'academy-lessons': {
    key: 'academy-lessons',
    steps: academyLessonsTourSteps,
    dialogTitle: 'Quick tour: Calendar',
    dialogDescription: 'See how to create lessons and navigate your schedule.',
    dialogIcon: CalendarDays,
  },
  'academy-lesson-session': {
    key: 'academy-lesson-session',
    steps: lessonSessionDetailTourSteps,
    dialogTitle: 'Quick tour: Session roster',
    dialogDescription:
      'Manage coaches and students for a single lesson occurrence — attendance, enrolment, and session actions.',
    dialogIcon: CalendarDays,
  },
  'academy-camps-create': {
    key: 'academy-camps-create',
    steps: campSingleCreateTourSteps,
    dialogTitle: 'Quick tour: Single camp',
    dialogDescription: 'Walk through the single-camp form — details, schedule, and pricing.',
    dialogIcon: CalendarDays,
    legacyPreferenceKey: 'tourCompletedCampManagement',
  },
  'academy-payments': {
    key: 'academy-payments',
    steps: [],
    dialogTitle: 'Quick tour: Payments',
    dialogDescription: 'Configure Stripe Connect or payment collection instructions.',
    dialogIcon: CreditCard,
    deferToPage: true,
  },
  'academy-payments-billing-report': {
    key: 'academy-payments-billing-report',
    steps: paymentsBillingReportTourSteps,
    dialogTitle: 'Quick tour: Billing Report',
    dialogDescription: 'Reconcile manual payments before Stripe Connect is active.',
    dialogIcon: CreditCard,
  },
  'academy-payments-starter': {
    key: 'academy-payments-starter',
    steps: paymentsStarterTourSteps,
    dialogTitle: 'Quick tour: Manual payments',
    dialogDescription: 'Billing report and payment setup before online checkout is active.',
    dialogIcon: CreditCard,
  },
  'academy-payments-dashboard': {
    key: 'academy-payments-dashboard',
    steps: paymentsDashboardTourSteps,
    dialogTitle: 'Quick tour: Payments Report',
    dialogDescription: 'Review Stripe charges and payouts.',
    dialogIcon: CreditCard,
  },
  'academy-payments-settings': {
    key: 'academy-payments-settings',
    steps: paymentsSettingsTourSteps,
    dialogTitle: 'Quick tour: Payment settings',
    dialogDescription: 'Payment instructions or Stripe Connect setup.',
    dialogIcon: CreditCard,
  },
  'academy-payments-discounts': {
    key: 'academy-payments-discounts',
    steps: paymentsDiscountsTourSteps,
    dialogTitle: 'Quick tour: Discounts',
    dialogDescription: 'Sibling and membership discounts.',
    dialogIcon: CreditCard,
  },
  'academy-payments-promo-codes': {
    key: 'academy-payments-promo-codes',
    steps: paymentsPromoCodesTourSteps,
    dialogTitle: 'Quick tour: Promo codes',
    dialogDescription: 'Create promotional lesson codes.',
    dialogIcon: CreditCard,
  },
  'academy-settings': {
    key: 'academy-settings',
    steps: academySettingsTourSteps,
    dialogTitle: 'Quick tour: Academy settings',
    dialogDescription: 'Academy name, age groups, and advanced lesson policies.',
    dialogIcon: Building2,
  },
  'academy-students-list': {
    key: 'academy-students-list',
    steps: [],
    dialogTitle: 'Quick tour: Students',
    dialogDescription: 'Search your roster, add students manually, and import CSVs.',
    dialogIcon: Users,
    deferToPage: true,
  },
  'academy-student-detail': {
    key: 'academy-student-detail',
    steps: studentDetailTourSteps,
    dialogTitle: 'Quick tour: Student profile',
    dialogDescription: 'Profile tabs, attendance, and student actions.',
    dialogIcon: Users,
  },
  'academy-bookings-list': {
    key: 'academy-bookings-list',
    steps: bookingsListTourSteps,
    dialogTitle: 'Quick tour: Bookings',
    dialogDescription: 'Filter, confirm, and manage lesson and camp bookings.',
    dialogIcon: ClipboardList,
  },
  'club-calendar': {
    key: 'club-calendar',
    steps: clubCalendarTourSteps,
    dialogTitle: 'Quick tour: Facility calendar',
    dialogDescription: 'See court hire, closures, and play events across your facility schedule.',
    dialogIcon: CalendarDays,
  },
  'club-court-hire': {
    key: 'club-court-hire',
    steps: [],
    dialogTitle: 'Quick tour: Court hire',
    dialogDescription:
      'Walk through bookings, hire rates, settings, and creating your first rate.',
    dialogIcon: Tag,
    deferToPage: true,
  },
  'club-court-hire-bookings': {
    key: 'club-court-hire-bookings',
    steps: clubCourtHireBookingsTourSteps,
    dialogTitle: 'Quick tour: Court hire bookings',
    dialogDescription: 'Review guest and member court rentals.',
    dialogIcon: Tag,
  },
  'club-court-hire-rates': {
    key: 'club-court-hire-rates',
    steps: clubCourtHireRatesTourSteps,
    dialogTitle: 'Quick tour: Hire rates',
    dialogDescription: 'Create session lengths and prices for court hire.',
    dialogIcon: Tag,
  },
  'club-court-hire-settings': {
    key: 'club-court-hire-settings',
    steps: clubCourtHireSettingsTourSteps,
    dialogTitle: 'Quick tour: Court hire settings',
    dialogDescription: 'Configure guest checkout and public booking rules.',
    dialogIcon: Tag,
  },
  'club-court-hire-rate-create': {
    key: 'club-court-hire-rate-create',
    steps: courtHireRateCreateTourSteps,
    dialogTitle: 'Quick tour: Create hire rate',
    dialogDescription: 'Walk through duration, price, and court scope for a new rate.',
    dialogIcon: Tag,
  },
  'club-members': {
    key: 'club-members',
    steps: [],
    dialogTitle: 'Quick tour: Members',
    dialogDescription:
      'Walk through the roster, membership plans, settings, and creating a plan.',
    dialogIcon: Users,
    deferToPage: true,
  },
  'club-members-roster': {
    key: 'club-members-roster',
    steps: clubMembersRosterTourSteps,
    dialogTitle: 'Quick tour: Member roster',
    dialogDescription: 'Search members and manage memberships.',
    dialogIcon: Users,
  },
  'club-members-types': {
    key: 'club-members-types',
    steps: clubMembersTypesTourSteps,
    dialogTitle: 'Quick tour: Membership plans',
    dialogDescription: 'Create plans members can join or be assigned to.',
    dialogIcon: Users,
  },
  'club-members-settings': {
    key: 'club-members-settings',
    steps: clubMembersSettingsTourSteps,
    dialogTitle: 'Quick tour: Membership settings',
    dialogDescription: 'Configure online join and membership year options.',
    dialogIcon: Users,
  },
  'club-members-type-create': {
    key: 'club-members-type-create',
    steps: membershipTypeCreateTourSteps,
    dialogTitle: 'Quick tour: New membership plan',
    dialogDescription: 'Walk through pricing, seats, and plan rules.',
    dialogIcon: Users,
  },
  'club-inventory': {
    key: 'club-inventory',
    steps: [],
    dialogTitle: 'Quick tour: Inventory',
    dialogDescription: 'Walk through items, hires, and creating an inventory item.',
    dialogIcon: Package,
    deferToPage: true,
  },
  'club-inventory-items': {
    key: 'club-inventory-items',
    steps: clubInventoryItemsTourSteps,
    dialogTitle: 'Quick tour: Inventory items',
    dialogDescription: 'Add and manage hireable or stock-tracked items.',
    dialogIcon: Package,
  },
  'club-inventory-hires': {
    key: 'club-inventory-hires',
    steps: clubInventoryHiresTourSteps,
    dialogTitle: 'Quick tour: Equipment hires',
    dialogDescription: 'Review active and completed equipment loans.',
    dialogIcon: Package,
  },
  'club-inventory-item-create': {
    key: 'club-inventory-item-create',
    steps: inventoryItemCreateTourSteps,
    dialogTitle: 'Quick tour: Add inventory item',
    dialogDescription: 'Walk through quantity, hire pricing, and stock options.',
    dialogIcon: Package,
  },
};

export function getPageTourDefinition(key: string | null | undefined): PageTourDefinition | null {
  if (!key || !(key in PAGE_TOUR_REGISTRY)) {
    return null;
  }
  return PAGE_TOUR_REGISTRY[key as PageTourKey];
}

export const ALL_PAGE_TOUR_KEYS = Object.keys(PAGE_TOUR_REGISTRY) as PageTourKey[];
